import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/config/queryClient';
import { isAwaitingLinkedInDestination } from '@/lib/connections/destination-options';
import { getPlatformLabel } from '@/lib/platforms/identity';
import type { PlatformId } from '@/lib/platforms/registry';
import { PREVIEW_ACCOUNT_MESSAGE } from '@/lib/preview-account';
import { publishService } from '@/services/data/PublishService';
import { showErrorToast, showInfoToast, showSuccessToast, showWarningToast } from '@/utils/toast';

/** "Instagram", "Instagram and LinkedIn", "Instagram, LinkedIn, and TikTok". */
export function formatPlatformList(platforms: string[]): string {
  const labels = platforms.map((p) => getPlatformLabel(p));
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/**
 * usePublishNow — "Post now" outside the chat (A3).
 *
 * Wraps PublishService.publishNow (the real platform-dispatching publish, NOT
 * the status-only PostService.publishPost) and refreshes the posts queries so
 * the inspector + calendar reflect the new `published` (or reverted `draft`)
 * state. Surfacing a connect-prompt vs generic failure is the caller's job via
 * the thrown ServiceError code.
 */
export const usePublishNow = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      postId,
      platform,
      confirmRepostPlatforms,
      sendingOwedPlatforms,
    }: {
      postId: string;
      /** A single platform (existing call sites) or a ready-subset list (multi-platform commit, P0-1). */
      platform: PlatformId | PlatformId[];
      /** The platforms the author checked and chose to post again (`shared/provider-outcome.ts`). */
      confirmRepostPlatforms?: string[];
      /** Sends to siblings of a target still being checked. */
      sendingOwedPlatforms?: boolean;
    }) => {
      const response =
        confirmRepostPlatforms || sendingOwedPlatforms
          ? await publishService.publishNow(postId, platform, {
              confirmRepostPlatforms,
              sendingOwedPlatforms,
            })
          : await publishService.publishNow(postId, platform);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data!;
    },
    onSuccess: (result) => {
      if (result.held) {
        showSuccessToast(
          'Sent for review',
          'This post is awaiting approval before it can publish.',
        );
      } else if (result.outcome === 'unconfirmed') {
        // The provider accepted it but returned no usable id. It is very likely
        // live, so "Publishing failed" would be its own false claim — say what
        // we actually know and send the user to look.
        showWarningToast(
          `Couldn't confirm the post on ${formatPlatformList(result.unconfirmedPlatforms)}`,
          'Check it before retrying.',
        );
      } else if (result.outcome === 'partial') {
        // Deliberately NOT a success toast: some platforms did not receive it,
        // and "Posted" would be a false claim about those.
        showWarningToast(
          `Posted to ${formatPlatformList(result.publishedPlatforms)}`,
          [
            result.failedPlatforms.length > 0
              ? `Couldn't post to ${formatPlatformList(result.failedPlatforms)} — try again from the post.`
              : null,
            result.unconfirmedPlatforms.length > 0
              ? `Couldn't confirm ${formatPlatformList(result.unconfirmedPlatforms)} — check it before retrying.`
              : null,
          ]
            .filter(Boolean)
            .join(' '),
        );
      } else {
        showSuccessToast(
          'Posted',
          `Published to ${formatPlatformList(result.publishedPlatforms)}.`,
        );
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.posts.all, exact: false });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.posts.detail(result.postId),
        exact: false,
      });
      // The open editor reads `usePost`, keyed ['post', postId, userId] — outside
      // the ['posts'] family above. Without this the status pill kept saying
      // "Scheduled" and the CTA kept saying "Post now" after a successful publish.
      void queryClient.invalidateQueries({ queryKey: ['post', result.postId], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['post-open-targets', result.postId] });
      // `post-cover-targets` has a 15s staleTime and gates the video-cover CTA
      // (PostBody's coverBlocker) — publish flips target_first_ready, and
      // without this invalidation the CTA could stay disabled for up to 15s
      // after a successful publish. `exact: false` matches every key that
      // starts with ['post-cover-targets', result.postId, ...] regardless of
      // the trailing target_first_ready/serverPlatformsKey segments.
      void queryClient.invalidateQueries({
        queryKey: ['post-cover-targets', result.postId],
        exact: false,
      });
    },
    onError: (error: Error) => {
      if (isAwaitingLinkedInDestination(error)) return;
      if (error.message === PREVIEW_ACCOUNT_MESSAGE) {
        showInfoToast(PREVIEW_ACCOUNT_MESSAGE);
        return;
      }
      showErrorToast('Could not publish', error.message);
    },
  });

  return {
    publishNow: mutation.mutateAsync,
    isPublishing: mutation.isPending,
  };
};
